const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.trustoffice.app';
export const API = `${BACKEND_URL}/api`;

// Helper to get auth headers including localStorage token as fallback
export const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Robust POST request for auth endpoints (works on mobile)
export const authPost = async (endpoint, data) => {
  const url = `${API}${endpoint}`;
  const body = JSON.stringify(data);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: body
    });
    
    // Read response as text first (more reliable on mobile)
    const responseText = await response.text();
    
    let responseData = {};
    if (responseText) {
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response:', responseText);
        throw new Error('Invalid server response');
      }
    }
    
    if (!response.ok) {
      throw new Error(responseData.detail || `Request failed with status ${response.status}`);
    }
    
    return responseData;
  } catch (error) {
    // Check if it's a network error vs server error
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error - please check your connection');
    }
    throw error;
  }
};

// Single 401 interceptor: on an access-token failure, attempt exactly ONE
// refresh (POST /auth/refresh using the httpOnly refresh cookie), then retry
// the original request once. If refresh fails or is absent, clear the session
// token and dispatch `session-expired` so AuthContext redirects to login.
let _refreshInFlight = null;

const _attemptRefresh = async () => {
  if (!_refreshInFlight) {
    _refreshInFlight = fetch(`${API}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(async (r) => {
        if (!r.ok) throw new Error('refresh failed');
        const data = await r.json().catch(() => ({}));
        if (data.token) localStorage.setItem('auth_token', data.token);
        return true;
      })
      .finally(() => { _refreshInFlight = null; });
  }
  return _refreshInFlight;
};

// Generic fetch with auth
export const fetchWithAuth = async (endpoint, options = {}) => {
  const defaultOptions = {
    credentials: 'include',
    headers: getAuthHeaders()
  };

  // Guard: callers may pass an absolute URL (e.g. `${API_BASE}/api/...`).
  // Strip the backend origin + /api prefix so we never produce `${API}https://...`.
  // Then strip any leading `/api/` that SURVIVED the origin strip — callers who
  // write `${API_BASE}/api/trusts/...` (origin-style) leave a bare '/api/...' path
  // here, and appending API would double the prefix -> '/api/api/...' -> 404
  // (Brent Roufs 2026-10-09, email-archive/status lockout loop).
  let path = endpoint;
  if (typeof path === 'string' && path.startsWith(BACKEND_URL)) {
    path = path.slice(BACKEND_URL.length);
  }
  if (typeof path === 'string' && path.startsWith(`${API}/`)) {
    path = path.slice(API.length);
  }
  if (typeof path === 'string' && path.startsWith('/api/')) {
    path = path.slice('/api'.length);
  }

  const doFetch = () =>
    fetch(`${API}${path}`, {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers
      }
    });

  const response = await doFetch();
  
  // Handle 401 Unauthorized (session expired - token invalid or missing)
  if (response.status === 401) {
    const hadToken = Boolean(localStorage.getItem('auth_token'));
    // Attempt exactly one refresh + retry of the original request.
    try {
      await _attemptRefresh();
      const retry = await doFetch();
      if (retry.status !== 401) return retry;
    } catch (_e) {
      // refresh failed — fall through to session-expired handling
    }
    // Refresh failed or retry still 401: clear session and redirect to login.
    if (hadToken || localStorage.getItem('auth_token')) {
      localStorage.removeItem('auth_token');
      window.dispatchEvent(new CustomEvent('session-expired'));
    }
    return response;
  }

  // Handle 402 Payment Required (subscription expired - blocks all access)
  if (response.status === 402) {
    window.dispatchEvent(new CustomEvent('subscription-expired'));
  }

  // Handle 403 Forbidden (read-only mode - blocks write operations)
  // We check the X-Subscription-Status header to avoid consuming the body
  if (response.status === 403) {
    const subscriptionStatus = response.headers.get('X-Subscription-Status');
    if (subscriptionStatus) {
      // This is a subscription-related 403, dispatch event
      window.dispatchEvent(new CustomEvent('subscription-readonly', {
        detail: {
          status: subscriptionStatus
        }
      }));
    }
  }
  
  return response;
};

// Helper to check if a response is a read-only error
export const isReadOnlyError = (response) => {
  return response.status === 403;
};

// Helper to extract error message from response
export const getErrorMessage = async (response) => {
  try {
    const data = await response.json();
    return data.detail || 'An error occurred';
  } catch {
    return 'An error occurred';
  }
};
